/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { ReconPowderListBatchResponse } from "../../components/admin/reconciliationMediaPreparation/reconcileMediaPowder/reconcilePowder.types";
import { authToken, authUserID, axiosInstance } from "../axiosConfig";
import baseUrl from "..";
import { AxiosResponse } from "axios";
import {
	BatchReconciliationRequest,
	ContainerReconcilationLotsResponse,
	PhyReconAdjustmentReasonsResponse,
	PowderReconcilationLotsResponse,
	ReconBaseLot,
	ReconContainerLot,
	ReconContainerPostRes,
	ReconPowderLot,
	ReconSolutionLot,
	SolutionReconcilationLotsResponse,
} from "../../components/admin/reconciliationMediaPreparation/StartPhysicalReconciliation/StartReconciliation.types";
import { ReconSolutionBatchListResponse } from "../../components/admin/reconciliationMediaPreparation/reconcileSolution/reconcileSolution.types";
import { ReconContainerListBatchResponse } from "../../components/admin/reconciliationMediaPreparation/reconcileContainer/reconcileContainer.types";
import {
	MBReconPayload,
	ReconMediaBatchDetailResponse,
	ReconMediaBatchListResponse,
} from "../../components/admin/reconciliationMediaPreparation/reconcileMediaBatch/reconcileMediaBatch.types";

const HEADERS = {
	"x-auth-token": authToken,
	"x-userid": authUserID,
};

// Builds the URL for API requests, removing undefined or empty string parameters.
const buildUrl = (
	apiEndpoint: string,
	params: Record<string, string | number | undefined>,
): string => {
	const filtered = Object.fromEntries(
		Object.entries(params).filter(([_, v]) => v !== undefined && v !== ""),
	);
	return `${baseUrl}/${apiEndpoint}?${new URLSearchParams(filtered as Record<string, string>)}`;
};

// Functions below are for getting batches for reconciliation; for mediapowder, solution, and container.
const getReconPowderBatchList = async (
	urlParams: any,
): Promise<ReconPowderListBatchResponse> => {
	try {
		const url = buildUrl(
			"media_powder_reconciliation/list_batches",
			urlParams,
		);
		const response: AxiosResponse<ReconPowderListBatchResponse> =
			await axiosInstance.get(url, { headers: HEADERS });
		return response.data;
	} catch (err) {
		throw err;
	}
};

const getReconSolutionBatchList = async (
	urlParams: any,
): Promise<ReconSolutionBatchListResponse> => {
	try {
		const url = buildUrl(
			"media_solution_reconciliation/list_batches",
			urlParams,
		);
		const response: AxiosResponse<ReconSolutionBatchListResponse> =
			await axiosInstance.get(url, { headers: HEADERS });
		return response.data;
	} catch (err) {
		throw err;
	}
};

const getReconContainerBatchList = async (
	urlParams: any,
): Promise<ReconContainerListBatchResponse> => {
	try {
		const url = buildUrl("container_reconciliation/list_batches", urlParams);
		const response: AxiosResponse<ReconContainerListBatchResponse> =
			await axiosInstance.get(url, { headers: HEADERS });
		return response.data;
	} catch (err) {
		throw err;
	}
};

const getReconMediaBatchList = async (
	urlParams: any,
): Promise<ReconMediaBatchListResponse> => {
	try {
		const url = buildUrl(
			"media_batch_reconciliation/list_batches",
			urlParams,
		);
		const response: AxiosResponse<ReconMediaBatchListResponse> =
			await axiosInstance.get(url, { headers: HEADERS });
		return response.data;
	} catch (err) {
		throw err;
	}
};

// Helper function to add a "physical [unit]" column/field to each of the lots regardless of the lot type
// as long as it extends the ReconBaseLot.
const createLot = <T extends ReconBaseLot>(data: Partial<T>): T =>
	({
		physical_quantity: data.system_quantity,
		...data,
	}) as T;

// Get the lots corresponding to the batch

const getReconPowderLotList = async (
	batchId: string,
): Promise<PowderReconcilationLotsResponse> => {
	try {
		const url = buildUrl(
			"media_powder_reconciliation/get_lots_for_reconciliation",
			{ batch_id: batchId },
		);
		const response: AxiosResponse<PowderReconcilationLotsResponse> =
			await axiosInstance.get(url, { headers: HEADERS });
		return {
			...response.data,
			data: {
				...response.data.data,
				lots: response.data.data.lots.map(createLot<ReconPowderLot>),
			},
		};
	} catch (err) {
		throw err;
	}
};

const getReconSolutionLotList = async (
	batchId: string,
): Promise<SolutionReconcilationLotsResponse> => {
	try {
		const url = buildUrl(
			"media_solution_reconciliation/get_lots_for_reconciliation",
			{ batch_id: batchId },
		);
		const response: AxiosResponse<SolutionReconcilationLotsResponse> =
			await axiosInstance.get(url, { headers: HEADERS });
		return {
			...response.data,
			data: response.data.data.map(createLot<ReconSolutionLot>),
		};
	} catch (err) {
		throw err;
	}
};

const getReconContainerLotList = async (
	batchId: string,
): Promise<ContainerReconcilationLotsResponse> => {
	try {
		const url = buildUrl(
			"container_reconciliation/get_lots_for_reconciliation",
			{ batch_id: batchId },
		);
		const response: AxiosResponse<ContainerReconcilationLotsResponse> =
			await axiosInstance.get(url, { headers: HEADERS });
		return {
			...response.data,
			data: {
				...response.data.data,
				lots: response.data.data.lots.map(createLot<ReconContainerLot>),
			},
		};
	} catch (err) {
		throw err;
	}
};

const buildResourceArray = (data: ReconMediaBatchDetailResponse["data"]) => {
  const rawResources = [
    data.powder_resources?.[0] && {
      source: data.powder_resources[0],
      unit: "g",
      resource_type: "POWDER",
      planned_quantity: data.powder_resources[0].actual_quantity,
    },
    data.solution_resources?.[0] && {
      source: data.solution_resources[0],
      unit: "mL",
      resource_type: "SOLUTION",
      planned_quantity: data.solution_resources[0].actual_quantity,
    },
    data.container_resource && {
      source: data.container_resource,
      unit: "units",
      resource_type: "CONTAINER",
      planned_quantity: data.container_resource.planned_quantity,
    },
  ].filter(Boolean);

  return rawResources.map(({ source, unit, resource_type, planned_quantity }) => ({
    resource_name: source.resource_name,
    lot_reference: source.lot_reference,
    actual_quantity: source.actual_quantity,
    planned_quantity,
    unit,
    impact: source.impact || null,
    resource_type,
  }));
};

const getReconMediaBatchDetailResources = async (
	urlParams: any,
): Promise<ReconMediaBatchDetailResponse> => {
	try {
		const url = buildUrl("media_batch_reconciliation/init_data", urlParams);
		const response: AxiosResponse<ReconMediaBatchDetailResponse> =
			await axiosInstance.get(url, { headers: HEADERS });
		return {
			...response.data,
			data: {
				...response.data.data,
				resources: buildResourceArray(response.data.data),
			},
		};
	} catch (err) {
		throw err;
	}
};

// Post request to save a batch of reconciliation media data.

const postReconPowder = async (
	payload: BatchReconciliationRequest,
): Promise<boolean> => {
	try {
		const url = buildUrl("media_powder_reconciliation/save_batch", {});
		const response = await axiosInstance.post(url, payload, {
			headers: HEADERS,
		});
		return response.status === 200;
	} catch (err) {
		throw err;
	}
};

const postReconSolution = async (
	payload: BatchReconciliationRequest,
): Promise<boolean> => {
	try {
		const url = buildUrl("media_solution_reconciliation/save_batch", {});
		const response = await axiosInstance.post(url, payload, {
			headers: HEADERS,
		});
		return response.status === 200;
	} catch (err) {
		throw err;
	}
};

const postReconContainer = async (
	payload: BatchReconciliationRequest,
): Promise<ReconContainerPostRes> => {
	try {
		const url = buildUrl("container_reconciliation/save_batch", {});
		const response: AxiosResponse<ReconContainerPostRes> =
			await axiosInstance.post(url, payload, { headers: HEADERS });
		return response.data;
	} catch (err) {
		throw err;
	}
};

const postMediaReconciliationBatch = async (
	payload: MBReconPayload,
): Promise<any> => {
	try {
		const url = buildUrl("media_batch_reconciliation/save", {});
		const response = await axiosInstance.post(url, payload, {
			headers: HEADERS,
		});
		return response.data;
	} catch (err) {
		throw err;
	}
};

// Get the media adjustment reasons according to the category;
const getReconMediaAdjustmentReasons = async (
	category: string,
): Promise<PhyReconAdjustmentReasonsResponse> => {
	try {
		const url = buildUrl(
			"media_powder_reconciliation/list_adjustment_reasons",
			{
				page: 1,
				size: 50,
				sort: "id",
				order: "asc",
				category,
			},
		);
		const response: AxiosResponse<PhyReconAdjustmentReasonsResponse> =
			await axiosInstance.get(url, { headers: HEADERS });
		return response.data;
	} catch (err) {
		throw err;
	}
};

export const mediaReconciliationService = {
	getReconPowderBatchList,
	getReconSolutionBatchList,
	getReconContainerBatchList,
	getReconMediaBatchList,

	getReconPowderLotList,
	getReconSolutionLotList,
	getReconContainerLotList,
	getReconMediaBatchDetailResources,

	postReconPowder,
	postReconSolution,
	postReconContainer,
	postMediaReconciliationBatch,

	getReconMediaAdjustmentReasons,
};
